import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, Container} from '@mui/material'
import {getProductLineColors} from '@progynyglobal/pg-ui-library'

import PageLoader from '@progyny/components/PageLoader'
import {useConsultationsDetailQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import AssessmentFormBuilder, {
  FormPage,
} from '@progyny/pages/Consultations/components/AssessmentFormBuilder'
import ConsultationOverlayHeader from '@progyny/pages/Consultations/components/ConsultationOverlayHeader'

interface PregnancyAssessmentProps {
  consultationSlug: string
  basePath: string
}

const PregnancyAssessment: React.FC<PregnancyAssessmentProps> = ({
  consultationSlug,
  basePath,
}) => {
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {data, loading} = useConsultationsDetailQuery({
    variables: {slug: consultationSlug},
    skip: !consultationSlug,
  })

  // Define form pages with declarative question definitions
  const pages: FormPage[] = useMemo(
    () => [
      // Page 1: Support preferences
      {
        id: 'support_preferences',
        questions: [
          {
            id: 'q1',
            type: 'radio' as const,
            title: 'How would you like to receive support?',
            question: 'How would you like to receive support?',
            group: 'support_preferences',
            options: [
              {
                value: 'structured_programs',
                label: t(
                  'consultations_postpartum_postpartum_assessment_i_would_like_structured'
                ),
              },
              {
                value: 'resources',
                label: t(
                  'consultations_menopause_menopause_assessment_i_would_like_access_to'
                ),
              },
              {
                value: 'midwife_call',
                label: t(
                  'consultations_postpartum_postpartum_assessment_i_would_like_oneonone_c'
                ),
              },
              {
                value: 'other',
                label: t('help_categories_other'),
              },
            ],
          },
        ],
      },
      // Page 2: Depression screening
      {
        id: 'depression_screening',
        questions: [
          {
            id: 'q2',
            type: 'rating_table' as const,
            title:
              'Over the last 2 weeks, how much has this issue bothered you?',
            question: 'Depression screening assessment',
            group: 'depression_screening',
            items: [
              {
                id: 'little_interest',
                label: t(
                  'consultations_menopause_menopause_assessment_little_interest_or_pleasu'
                ),
              },
              {
                id: 'feeling_down',
                label: t(
                  'consultations_menopause_menopause_assessment_feeling_down_depressed_or'
                ),
              },
            ],
            columns: [
              {
                value: 'not_at_all',
                label: t(
                  'consultations_menopause_menopause_assessment_not_at_all'
                ),
              },
              {
                value: 'several_days',
                label: t(
                  'consultations_menopause_menopause_assessment_several_days'
                ),
              },
              {
                value: 'more_than_half',
                label: t(
                  'consultations_menopause_menopause_assessment_more_than_half_days'
                ),
              },
              {
                value: 'nearly_everyday',
                label: t(
                  'consultations_menopause_menopause_assessment_nearly_everyday'
                ),
              },
            ],
          },
        ],
      },
      // Page 3: Anxiety screening
      {
        id: 'anxiety_screening',
        questions: [
          {
            id: 'q3',
            type: 'rating_table' as const,
            title:
              'Over the last 2 weeks, how much has this issue bothered you?',
            question: 'Anxiety screening assessment',
            group: 'anxiety_screening',
            items: [
              {
                id: 'feeling_nervous',
                label: t(
                  'consultations_menopause_menopause_assessment_feeling_nervous_anxious_o'
                ),
              },
              {
                id: 'not_able_to_stop',
                label: t(
                  'consultations_menopause_menopause_assessment_not_being_able_to_stop'
                ),
              },
            ],
            columns: [
              {
                value: 'not_at_all',
                label: t(
                  'consultations_menopause_menopause_assessment_not_at_all'
                ),
              },
              {
                value: 'several_days',
                label: t(
                  'consultations_menopause_menopause_assessment_several_days'
                ),
              },
              {
                value: 'more_than_half',
                label: t(
                  'consultations_menopause_menopause_assessment_more_than_half_days'
                ),
              },
              {
                value: 'nearly_everyday',
                label: t(
                  'consultations_menopause_menopause_assessment_nearly_everyday'
                ),
              },
            ],
          },
        ],
      },
    ],
    [t]
  )

  if (loading) {
    return (
      <Box minHeight={140}>
        <PageLoader position={'center'} />
      </Box>
    )
  }

  if (!data?.consultations?.consultation?.id) {
    return null
  }

  const consultation = data.consultations.consultation

  return (
    <Container maxWidth='xl'>
      <ConsultationOverlayHeader
        title={t(
          'consultations_pregnancy_pregnancy_assessment_pregnancy_wellbeing_asses'
        )}
        description={t(
          'consultations_pregnancy_pregnancy_assessment_please_help_us_understand'
        )}
        colors={colors}
      />
      <AssessmentFormBuilder
        consultationId={consultation.id}
        consultationSlug={consultationSlug}
        basePath={basePath}
        pages={pages}
      />
    </Container>
  )
}

export default PregnancyAssessment
